// TESSERAX — ÉTAT DE JEU MUTABLE PARTAGÉ (le « trio entrelacé » règles + boucle + état).
// Extrait de main.ts (phase 3 du plan docs/main-split-plan.md), iso-comportement.
//
// Les ~160 variables de closure de l'IIFE historique vivent désormais ICI, champs d'un
// objet unique muté en place (`state.score = …`) — les imports ES étant en lecture seule,
// c'est le seul mécanisme qui permette aux futurs modules (rules, flow, replay, input, hud)
// de PARTAGER des réassignations. Le renommage `x → state.x` de main.ts a été fait par
// résolution de SYMBOLES (API compilateur TS) : chaînes, clés d'objet et locales masquantes
// sont intactes ; le golden-replay (tests/golden.test.ts) verrouille l'iso-comportement.
//
// Ce module ne dépend d'AUCUN autre module du shell (state ← audio/scene ← rules ← loop) ;
// il n'importe que du cœur pur. Les initialiseurs sont EXACTEMENT ceux de main.ts —
// y compris les `undefined` d'avant-première-partie (score, grid…), préservés tels quels.
// Les types fins (Piece, Grid, Relic…) arrivent en phase 7 (durcissement) — d'où les `as any`.

import { ZONE_MS, RELIC_CHARGE_PER_EVENT } from '../core/constants';
import { loadMeta } from '../core/meta';
import { modeDef } from '../core/modes';

export const state = {
  // ZONE_TIERS / zoneTier proviennent de ./core/zone.
  grid: undefined as any,
  current: undefined as any,
  hold: null as any,
  holdUsed: false as any,
  // LE PLI (tous modes, cf. core/fold + foldMode) : tranche INACTIVE superposée (accumulateur fantôme) +
  // tranche active courante. Hors Pli (tutoriel scripté), gridV reste vide/inerte → chemin identique au
  // puits simple, rendu inchangé. runFolds/runTesseras = compteurs de run 4D.
  gridV: null as any,
  activeSlice: 'endroit' as any,
  runFolds: 0 as any,
  runTesseras: 0 as any,
  // STRATES (mode 'strates', cf. core/strates) : index d'ascension + référentiel par strate (les
  // objectifs se mesurent en DELTA depuis l'entrée dans la strate) + meilleurs chaîne/continuum de la strate.
  ascent: 0 as any,
  curStratum: null as any,
  strateBase: null as any,
  strateChainBest: 0 as any,
  strateB2bBest: 0 as any,
  strateOv: false as any,
  runAscentRecord: false as any,
  ngPlus: 0 as any,
  pendingStrateNg: 0 as any,
  // MOT-SESSION-RECAP-001 — accumulateurs de SESSION (réinitialisés au chargement de page) :
  // de quoi montrer « cette session : N parties, meilleur grade, +cristaux, reliques » au
  // retour menu (le temps investi sur plusieurs runs se CAPITALISE visiblement).
  sessRuns: 0 as any,
  sessRelics: 0 as any,
  sessGain: 0 as any,
  sessBestScore: 0 as any,
  sessBestGrade: '' as any,
  lastRunVictory: false as any,
  // File « suivant » : on garde QUEUE_LEN pièces à l'avance (best-in-class).
  // L'aperçu vedette montre nextQueue[0] ; la bande compacte montre la suite.
  // QUEUE_LEN vient de core/constants (SOURCE UNIQUE vif↔sim) : une file divergente décale
  // tout le flux de pièces de la re-simulation anti-triche → rejet massif de scores légitimes.
  nextQueue: [] as any,
  score: undefined as any,
  slabsTotal: undefined as any,
  totalLines: undefined as any,
  level: undefined as any,
  prevLevel: undefined as any,
  running: false as any,
  paused: false as any,
  choosing: false as any,
  lastDrop: 0 as any,
  pauseStart: 0 as any,
  _ending: false as any,
  _lastStart: 0 as any,
  _autoPaused: false as any,
  meter: 0 as any,
  charges: 0 as any,
  comboStreak: 0 as any,
  // BANQUE DE LIGNES → charges de Bascule (cf. core/pressure.linesPerCharge) : on cumule les
  // lignes effacées (pondérées survie) ; chaque palier linesPerCharge(level) débloque 1 charge.
  // Plus on est profond, plus une charge coûte de lignes → fin du « tilt infini ». Reset par run.
  tiltBank: 0 as any,
  zoneActive: false as any,
  zoneEndsAt: 0 as any,
  zoneHardEnd: 0 as any,
  zoneClears: 0 as any,
  zoneGained: 0 as any,
  zoneBankRows: 0 as any,
  zoneBankSlabs: 0 as any,
  zoneMs: ZONE_MS as any,
  twistMode: false as any,
  twistY: 0 as any,
  landed: false as any,
  lockTimer: 0 as any,
  lockResets: 0 as any,
  resolving: false as any,
  // Jeton de GÉNÉRATION de run : incrémenté à chaque (re)démarrage / abandon / fin de partie.
  // Les résolutions différées (finishLock, maillons de cascade — des setTimeout de ~0,2 à 2,5 s)
  // capturent le jeton à l'armement et deviennent des no-op si la partie a changé entre-temps :
  // sans lui, un « Rejouer » pendant une cascade laissait le timer orphelin COMMETTRE le score de
  // l'ancienne run dans la nouvelle (commitResolve + spawn fantôme). Même contrat que bascPending.
  runGen: 0 as any,
  _pieceFallDist: 0 as any,
  _pileBounceAmp: 0.20 as any,
  // Hauteur de la plus haute colonne capturée À L'INSTANT du verrouillage d'une pièce qui va
  // effacer (pièce posée incluse, lignes pas encore retirées) → comparée à l'après-clear pour
  // reconnaître un DÉGAGEMENT (clutch clear, cf. core/clutch). Affichage seul, aucun score.
  clutchBefore: 0 as any,
  bestScore: 0 as any,
  themeIndex: 0 as any,
  starPulse: 0 as any,
  tipZone: false as any,
  tipTwist: false as any,
  gameStart: 0 as any,
  maxStreak: 0 as any,
  maxLevel: 1 as any,
  hitStop: 0 as any,
  rightReady: false as any,
  firstClearDone: false as any,
  syzShown: false as any,
  foldHintShown: false as any,
  spawnFlash: 0 as any,
  cachedMaxH: 0 as any,
  rotatePulse: 0 as any,
  lockFlash: 0 as any,
  landedWobble: 0 as any,
  prevCombo: 0 as any,
  levelFlash: 0 as any,
  tileFlash: 0 as any,
  floorFlash: 0 as any,
  lockPitchPlayed: -1 as any,
  // VRILLE (« spin clear ») : la pièce a-t-elle trouvé sa place finale par une rotation AVEC
  // recalage (wall-kick) ? `spinTuck` = dernière manœuvre = rotation kickée, ET aucun mouvement
  // depuis (tout déplacement réussi, même la chute, le casse → cf. move()). Capturé à la pose
  // dans `lockSpin` pour récompenser l'effacement par torsion (feat de TRAJECTOIRE du stacker).
  spinTuck: false as any,
  lockSpin: false as any,
  // Pulsations « vivantes » de la chambre (même idiome de décroissance que floorFlash/levelFlash) :
  // wallPulse = embrasement de la grille Tron à chaque effet basculé ; wallRip = ONDE SPHÉRIQUE qui jaillit
  // du POINT de clear et se propage dans toutes les directions (sol + parois) en s'estompant ; cornerFlare = coins.
  wallPulseAmt: 0 as any,
  wallRipAmt: 0 as any,
  wallRipR: 0 as any,
  cornerFlare: 0 as any,
  nodePulse: 0 as any,
  chamberE: 0 as any,
  // Lumière qui MONTE dans le puits (l'énergie/charge se lit sur les LIGNES, à la place de l'éclat des nœuds) ;
  // impact DIRECTIONNEL de Bascule (mur visé pendant le twist / mur frappé au SLAM) ; micro-rebond de la pile au SLAM.
  _fillLvl: 0 as any,
  hitAmt: 0 as any,
  _hitAxis: 0 as any,
  _hitSign: 1 as any,
  aimCharge: 0 as any,
  pileBounceT0: -1e9 as any,
  // ONDE d'énergie qui remonte les veines à CHAQUE action (le réacteur réagit) : amorcée dans wallPulse, montée dans la boucle.
  surgeAmt: 0 as any,
  surgeY0: 0 as any,
  surgeT0: -1e9 as any,
  dailyMode: false as any,
  seedStr: '' as any,
  playerName: 'Anon' as any,
  rng: Math.random as any,
  dailyMutator: null as any,
  // REJEU : chaque partie est SEEDÉE (mulberry32) → reproductible. `runSeed` est la graine de
  // run (date du jour pour le défi, fraîche sinon). On CAPTURE le journal d'entrées déterministe
  // (`runInputs`, cf. core/replay) pour le rejeu partageable. `replaying` = lecture d'un rejeu
  // (entrée humaine gelée, le curseur rejoue les évènements). Tout est affichage/relecture →
  // déterminisme du défi intact ; dégradation gracieuse (capture absente → jeu inchangé).
  runSeed: 0 as any,
  recording: false as any,
  runInputs: [] as any,
  replaying: false as any,
  replayObj: null as any,
  replayCursor: 0 as any,
  replayExpectedScore: 0 as any,
  replayName: '' as any,
  replayDoneShown: false as any,
  // RAIL DE RYTHME : trajectoire [tMs,score] de la meilleure tentative de la graine (rival),
  // enregistrement de la partie courante, et état d'affichage (cf. updatePaceRail / core/pace).
  paceRival: null as any,
  paceRivalFinal: 0 as any,
  runTraj: [] as any,
  paceLastT: -1e9 as any,
  paceLastScore: 0 as any,
  paceShown: false as any,
  paceRec: false as any,
  paceUiT: 0 as any,
  paceSource: '' as any,
  paceLeaderName: '' as any,
  debugSession: false as any,
  runScoreMult: 1 as any,
  runSpeedMult: 1 as any,
  runMeterMult: 1 as any,
  runComboMult: 1 as any,
  maxCharges: 3 as any,
  freeHold: false as any,
  offered: {} as any,
  runLevelOffset: 0 as any,
  // (montée d'instabilité : plomberie retirée — riseClock/riseRng/INSTAB_COLOR/doRise. La mécanique
  // était GELÉE (rise:false sur tous les modes, cf. core/modes) donc ce chemin était mort ; aucun
  // évènement de montée n'existe dans le codec de rejeu → aucune incidence sur la vérification.)
  // Flux RNG DÉDIÉ aux offres de reliques (draft) : découplé du flux de pièces → un reroll
  // ne décale PAS la séquence de pièces, donc le défi du jour reste identique pour tous
  // quel que soit le nombre de rerolls (équité du classement). Seedé en défi du jour.
  relicRng: Math.random as any,
  // Échelle de défis (cf. core/challenges) : run courante + garde anti-double-validation.
  curChallenge: null as any,
  challengeDone: false as any,
  pendingChallenge: null as any,
  endReplayAction: null as any,
  activeMissions: [] as any,
  bestChain: 0 as any,
  didPerfect: false as any,
  usedZone: false as any,
  _lastFloorTick: 0 as any,
  _inRunAchToasted: new Set() as any,
  // Méta roguelite — reliques actives + état persistant + stats de la run.
  lockDelayMult: 1 as any,
  activeRelics: [] as any,
  relicMem: {} as any,
  // Bannissement de draft (agency façon reroll, cf. offerRelic) : ids écartés du pool de tirage
  // POUR CETTE RUN uniquement — jamais meta.unlocked (persistant). Comme le reroll, aucun event
  // de rejeu : le CONTENU des offres n'est ni simulé ni vérifié (cf. core/verify), seul le pick l'est.
  bannedRelics: new Set() as any,
  bansLeft: 1 as any,
  nextClearMult: 1 as any,
  // Meilleur MOMENT de la run (plus gros gain d'une résolution) : fenêtre du clip de partage
  // (cf. game/clip). Repères d'AFFICHAGE seulement — jamais lus par le moteur ni le sim.
  clipBestGain: 0 as any,
  clipBestT: 0 as any,
  // Hook de capture du clip : posé par game/clip pendant l'enregistrement, consommé par la
  // boucle JUSTE APRÈS renderer.render (même frame → pas de preserveDrawingBuffer). Null sinon.
  _clipFrame: null as any,
  runBascules: 0 as any,
  runBasculeClears: 0 as any,
  runPerfects: 0 as any,
  runCross: 0 as any,
  // Jalon de score le plus haut déjà franchi cette run (cf. core/milestones) :
  // plancher de comparaison, ne recule jamais → chaque palier ne célèbre qu'une fois.
  msScore: 0 as any,
  // Chasse au record EN DIRECT : cible (meilleure marque DU MODE à l'entrée) + état de la pastille.
  chaseTarget: 0 as any,
  chaseState: 'none' as any,
  // CONTINUUM (back-to-back) : b2bCount = clears de maîtrise enchaînés sans rupture (état
  // courant), runMaxB2b = plus long continuum de la partie (pour stats / succès). cf. core/scoring.
  b2bCount: 0 as any,
  runMaxB2b: 0 as any,
  // Compteurs CUMULÉS de la run pour la méta des Voies (cf. core/voies) : nombre de clears de
  // CONTINUUM (b2b≥2, là où part emitRelic('continuum')) et lignes-équivalent BANQUÉES aux sorties
  // de Zone. Neutres au score, mais tenus en miroir strict avec core/sim (convention du repo).
  runContinuum: 0 as any,
  runZoneLines: 0 as any,
  // Dernier rang de maîtrise calculé (rank.ts) — partagé entre l'écran de fin et la carte de partage.
  lastRank: null as any,
  zenMode: false as any,
  beatPulse: 0 as any,
  pieceStats: {} as any,
  meta: loadMeta() as any,
  // ===================== Tutoriel jouable (Priorité 5) =====================
  // Leçons courtes et SCRIPTÉES (une phrase chacune) ; aucune relique/mission/méta
  // tant que placement, clears, cascade et Bascule ne sont pas compris. Tout est
  // gardé par `tutorialMode` → zéro impact sur une vraie partie.
  tutorialMode: false as any,
  tutFoldEnabled: false as any,
  tutStep: 0 as any,
  tutNoFall: false as any,
  tutAdvancing: false as any,
  // Accumulateurs réinitialisés à chaque leçon (alimentés par move/rotate/commit/bascule/zone).
  tut: {moves:0,rots:0,clears:0,maxChain:0,bascRepair:0,bascChain:0,zone:false,holesBefore:0} as any,
  // PIECES / THEMES proviennent de ./core/pieces ; mulberry32 / todaySeed de ./core/rng.
  bag: [] as any,
  /* ===== 2e balayage (phase 3b) : partagé mi-fichier — caméra, prefs, flow, cinématiques ===== */
  // GRID/HEIGHT/ZONE_MS/MAX_RESETS/NEXT_MULT_CAP/RELIC_CHARGE_PER_EVENT proviennent de ./core/constants.
  // La fenêtre de verrouillage (lockDelayMs) se resserre avec la Profondeur (cf. ./core/difficulty).
  // Mode du pouvoir Zone : 'bank' (banque + crescendo, défaut) ou 'classic' (paiement au fil de l'eau).
  // Surchargeable par ?zone=classic / ?zone=bank pour comparer (A/B) — SESSION SEULEMENT. L'ancienne
  // persistance localStorage('tesserax:zoneMode') est SUPPRIMÉE (et purgée) : le sim anti-triche
  // (core/sim) cible 'bank' en dur → un 'classic' collant faisait diverger la re-simulation et
  // rejetait SILENCIEUSEMENT tous les scores légitimes suivants (400 serveur). En 'classic' explicite,
  // on n'attache d'ailleurs plus de rejeu à la soumission (cf. runReplayStr) : score borné seulement.
  ZONE_MODE: 'bank' as any,
  // Commande tactile de déplacement : 'dpad' (croix directionnelle flottante — pas DISCRETS,
  // contrôle précis et prévisible, défaut), 'joy' (joystick analogique flottant) ou 'touch'
  // (glisser/taper direct — un doigt amène la pièce vers la colonne sous le doigt, positionnement
  // ABSOLU mais projection 2D→3D imprécise sur un puits en perspective orbitable, opt-in).
  // Surchargeable par ?pad=touch|joy|dpad ou localStorage('tesserax:padMode') pour l'A/B.
  PAD_MODE: 'dpad' as any,
  adminMode: false as any,
  debugStartDepth: 1 as any,
  /* ============ audio: sfx + generative music → src/game/audio.ts ============ */
  // Moteur audio EXTRAIT (phase 1 du découpage, cf. docs/main-split-plan.md) : factory
  // createAudio(ctx) — l'audio ne fait que LIRE l'état de jeu via ces getters (+ 2 callbacks
  // de pulsation visuelle). On DESTRUCTURE les fonctions publiques sous leurs noms historiques
  // → tous les sites d'appel (sfx/blip/haptic/stinger/…) restent inchangés.
  _inDanger: false as any,
  gpIndex: -1 as any,
  musicVol: 1 as any,
  sfxVol: 1 as any,
  shakePref: 1 as any,
  hapticsPref: true as any,
  qPref: 'auto' as any,
  muted: false as any,
  songPref: true as any,
  // Chambre préférée : un index 0..5 = la chambre équipée (Boutique), stable toute la
  // partie ; 'auto' = chambre par défaut (Azur). La rotation par profondeur est retirée (DA).
  worldPref: 'auto' as any,
  // Approfondissement de l'ENVERS (lueur de plafond inversée, ciel négatif, couture-miroir) : ON par défaut,
  // désactivable (A/B / lisibilité) via localStorage('tesserax:enversdeep'='0'). Cosmétique pur.
  enversDeepPref: true as any,
  // _dropping : pendant la rafale de hard-drop, on saute la reconstruction du fantôme/
  // faisceau/aperçu (jusqu'à 14 fois) — invisible (la pièce se verrouille aussitôt) mais
  // coûteux (allocations de meshes + scan de grille). Reconstruit une fois après la chute.
  _dropping: false as any,
  _e2eMoves: 0 as any,
  // Quand une RÉSOLUTION est ENREGISTRÉE pour rejeu visuel maillon par maillon (cf.
  // playCascade), la résolution synchrone doit rester MUETTE : ni sillage de chute, ni
  // tonalité de cascade ne sont émis à la volée (ils figeraient tout sur une seule frame).
  // Le rejeu les ressème au bon tempo, maillon après maillon. Hors enregistrement (Bascule,
  // Zone, montée), drapeau à false → comportement historique inchangé.
  _castNoTrail: false as any,
  // API fournie aux crochets de reliques : lit l'état, applique des effets concrets.
  // Budget de remboursement de charges PAR évènement (anti-empilement de reliques) : remis à
  // neuf au début de chaque emitRelic ; addCharge y puise. Borne le BURST d'un seul tour
  // (faille+quadrant+onde de choc… sur un même clear) sans tuer l'accrue au fil des tours.
  relicChargeBudget: RELIC_CHARGE_PER_EVENT as any,
  // ---- Aperçu de Bascule : montre la conséquence AVANT de valider (press → preview,
  // relâché → exécution). basculeHover retient la direction prévisualisée et en attente.
  basculeHover: null as any,
  // Filtre de profondeur (« peek ») : masque les couches HAUTES pour lire le fond du
  // puits. 0 = tout visible · 1 = on ne garde que ~60 % du bas · 2 = ~30 % du bas.
  // Seuil RELATIF à la hauteur réelle de la pile (sinon, sur une pile basse, un seuil
  // absolu ne masque rien → l'effet semble inexistant).
  peekState: 0 as any,
  /* ============ camera ============ */
  camR: 27 as any,
  camTheta: -Math.PI*0.78 as any,
  camPhi: 1.02 as any,
  shakeAmt: 0 as any,
  camPunch: 0 as any,
  fovPunch: 0 as any,
  portraitZoom: 1 as any,
  // Décalage de cadrage CINÉMATIQUE (Bascule) : ajouté PAR-DESSUS camTheta/camPhi dans updateCam, jamais
  // fusionné dedans → l'angle JOUEUR n'est pas muté (l'entrée garde la main) et le cadrage revient pile à sa place.
  camThetaFX: 0 as any,
  camPhiFX: 0 as any,
  // LE PLI — décalages caméra DÉDIÉS (ajoutés par-dessus, jamais fusionnés) : _foldTargetDip plonge le point visé
  // AVEC le puits pendant la traversée de membrane (pic sin, gated shakePref → net-zéro) ; camREnv/camPhiEnv/camYEnv
  // sont la REPOSE LENTE en Envers (pilotés par enversEnv, easing ~1–2 s) → la vue recule & se ré-angle dans la
  // dimension inversée, puis revient à la surface au retour. Tout revient à 0 (sin(π)=0 ; enversEnv→0 en KATA).
  _foldTargetDip: 0 as any,
  camREnv: 0 as any,
  camPhiEnv: 0 as any,
  camYEnv: 0 as any,
  camRFold: 0 as any,
  // ===================== Bascule CINÉMATIQUE =====================
  // Avant : un scalaire amorti (basculeTilt*=0.86) inclinait SEULEMENT les blocs ~150 ms. Désormais une VRAIE
  // timeline incline la CHAMBRE entière (chamberGroup) vers le mur visé, plus lente et plus ample, en
  // anticipation → penche → maintien → retour → léger dépassement. bascCine={t0,dur,nx,nz,amp,tgtT,tgtP} ou null.
  // nx/nz = signes d'axe de penche (selon dir) ; amp plie la magnitude + l'accessibilité (shakePref=0 → amp=0 =
  // aucun mouvement) ; tgtT/tgtP = cadrage « carré » visé par le décalage caméra (camThetaFX/camPhiFX).
  bascCine: null as any,
  // LE PLI — cinématique PROPRE (≠ Bascule) : pli géométrique de la chambre + balayage de phase + pulse négatif.
  // Canaux DISJOINTS de la Bascule (qui prend rotation.x/.z) : le Pli n'utilise QUE scale + rotation.y + position.y
  // → les deux cinématiques ne se marchent jamais dessus. {t0,dir,dur} : dir=+1 ANA (vers l'Envers), −1 KATA (retour).
  foldCine: null as any,
  // Bascule GLISSE+SLAM : la résolution (clears/score) est calculée à t=0 (grille finale) mais son RENDU est
  // différé — la pile glisse vers le mur pendant la penche, puis SLAM au pic. bascPending = charge différée ;
  // pileGlide = items {i,from,to} animés par instance ; activeFrom = départ du glissé de la pièce active.
  bascPending: null as any,
  pileGlide: null as any,
  activeFrom: null as any,
  viewIndex: 0 as any,
  viewTweening: false as any,
  camGoal: {t:-2.45,p:1.02,r:35} as any,
  dockH: 0 as any,
  // INVITATION DE CASCADE (feature 1a) : pendant le jeu normal, on scanne les 4 murs (core/grid.
  // bestBascule) et, si une CASCADE ≥3 est PRÉPARÉE et n'attend qu'un tilt, on l'annonce — la
  // Bascule devient un PAYOFF qu'on monte, plus seulement un correctif réactif. Cue persistant
  // (puce + halo du bouton Bascule) + UN toast à fort signal au moment où elle DEVIENT possible
  // (déclenché sur le bord, jamais à chaque frame). Garde-fous : muet hors partie / en tutoriel /
  // en mode Bascule (l'aperçu prend le relais) / en Zone / sans charge. LECTURE seule (bestBascule
  // travaille sur des copies) → aucun RNG, aucun score touché → déterminisme du défi intact.
  cascadeCue: null as any,
  _cascReadyChain: 0 as any,
  // Panneau Réglages : gèle la chute (comme une pause) mais laisse tourner la
  // musique → on entend les volumes changer en direct pendant le réglage.
  settingsOpen: false as any,
  curMode: 'free' as any,
  curModeDef: modeDef('free') as any,
  // ===== ONB-MEASURE-001 : instrumentation locale ANONYME de la 1re session (outille les playtests) =====
  // Écrit dans localStorage 'tesserax:onb' des repères de PREMIÈRE session : temps avant 1re action /
  // 1er clear / fin, issue, et progression dans le tutoriel. 100 % LOCAL, anonyme, AUCUN envoi réseau.
  // Timestamps via performance.now (jamais le RNG) + écritures gardées → zéro impact déterminisme/gameplay.
  // Lecture devtools : __onbReport() (table) · remise à zéro : __onbReset(). Cf. docs/onboarding-baseline.md.
  onbActive: false as any,
  howPendingMode: null as any,
  howPendingHint: null as any,
  /* ============ panneau Succès : objectifs à poursuivre (rétention) ============ */
  // Profil joueur D1 (rang par joueur, effectif, rareté des succès) — chargé une
  // fois au démarrage ; null tant qu'indisponible (dev/hors-ligne) → on dégrade.
  profileData: null as any,
  zoneEnv: 0 as any,
  vizClock: 0 as any,
  enversEnv: 0 as any,
  _foldSlow: 0 as any,
  /* ===== 3e balayage (phase 4) : entrées partagées avec la boucle/les règles ===== */
  _lastInvalid_unused: 0 as any,   // (placeholder de lisibilité — _lastInvalid reste local à input)
  _inputBuf: null as any, _inputBufT: 0 as any,          // tampon d'entrée pendant la résolution (consommé par lock/act)
  _nudgeAmt: 0 as any, _nudgeAxis: 0 as any, _nudgeSgn: 1 as any,   // « nudge » de refus (décru dans la boucle)
  dasPreset: 'standard' as any, dasDelay: 140 as any, arrRate: 35 as any,   // presets DAS/ARR (réglages ⚙)
};
